import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import './Auth.css';

// Google web button can't render in the iOS webview (capacitor://localhost
// origin is rejected by Google Identity Services). On native we show Apple
// Sign-In via the Capacitor plugin instead — App Store guideline 4.8 also
// requires Apple alongside any third-party social login.
const IS_NATIVE = Capacitor.isNativePlatform();

const Login = ({ onSwitchToSignup, onSwitchToForgotPassword, onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, loginWithGoogle, loginWithApple } = useAuth();

  const handleAppleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await SignInWithApple.authorize({
        clientId: 'com.ranga.app',
        redirectURI: 'https://www.ranga.com/auth/apple/callback',
        scopes: 'email name',
      });
      const { identityToken, givenName, familyName } = res.response || {};
      if (!identityToken) {
        setError('Apple Sign-In did not return a token.');
        setLoading(false);
        return;
      }
      const result = await loginWithApple({ identityToken, givenName, familyName });
      setLoading(false);
      if (result.success) {
        if (onSuccess) onSuccess(result.user);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setLoading(false);
      // User cancelled flows throw an error with code/message; don't surface
      // those as errors. Anything else is a real failure.
      if (err && /canceled|cancel/i.test(err.message || '')) return;
      console.error('Apple Sign-In error:', err);
      setError('Apple Sign-In failed. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);
    
    setLoading(false);

    if (result.success) {
      console.log('Login successful:', result.user);
      if (onSuccess) {
        onSuccess(result.user);
      }
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">Welcome to Ranga</h1>
        <h2 className="auth-subtitle">Login to your account</h2>
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="username">Username or Email</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username or email"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              disabled={loading}
            />
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {IS_NATIVE && (
          <>
            <div className="auth-divider">
              <span>OR</span>
            </div>
            <button
              type="button"
              className="apple-signin-button"
              onClick={handleAppleSignIn}
              disabled={loading}
            >
              <span aria-hidden="true" className="apple-signin-icon"></span>
              Sign in with Apple
            </button>
          </>
        )}

        {!IS_NATIVE && (
          <>
            <div className="auth-divider">
              <span>OR</span>
            </div>

            <div className="google-login-wrapper">
              <GoogleLogin
                onSuccess={async (credentialResponse) => {
                  setLoading(true);
                  const result = await loginWithGoogle(credentialResponse.credential);
                  setLoading(false);

                  if (result.success) {
                    console.log('Google login successful:', result.user);
                    if (onSuccess) {
                      onSuccess(result.user);
                    }
                  } else {
                    setError(result.error);
                  }
                }}
                onError={() => {
                  setError('Google Sign-In failed. Please try again.');
                }}
                useOneTap={false}
                theme="outline"
                size="large"
                text="signin_with"
                shape="rectangular"
                logo_alignment="left"
              />
            </div>
          </>
        )}

        <div className="auth-links">
          <button 
            onClick={onSwitchToForgotPassword}
            className="auth-link-text"
            disabled={loading}
          >
            Forgot Password?
          </button>
        </div>

        <div className="auth-switch">
          Don't have an account? {' '}
          <button 
            onClick={onSwitchToSignup}
            className="auth-link"
            disabled={loading}
          >
            Sign up
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
